// Global Instructions Rule Applied!
import { toast } from 'react-toastify'
import { TOAST_CONFIG } from './constants.ts'

// Validation rule types
export const VALIDATION_TYPES = {
  REQUIRED: 'required',
  EMAIL: 'email',
  POSITIVE_NUMBER: 'positive_number',
  CUSTOM: 'custom',
  ASYNC: 'async'
} as const

export type ValidationType = (typeof VALIDATION_TYPES)[keyof typeof VALIDATION_TYPES]

// Validation rule options
export interface ValidationRuleOptions {
  fieldKey?: string
  validator?: (value: any, fieldName: string, record: any) => string | null
  asyncValidator?: (record: any) => Promise<string | null> | string | null
}

// Validation rule
export interface ValidationRule {
  type: ValidationType
  fieldName: string
  options: ValidationRuleOptions
  validate: (record: any) => string | null
}

// Validation result
export interface ValidationResult {
  isValid: boolean
  errors: Record<string, string>
  firstErrorField?: string | null
}

// Common validation functions
const VALIDATORS = {
  required: (value: any, fieldName: string = 'Field'): string | null => {
    if (!value || (typeof value === 'object' && !value.value)) {
      return `${fieldName} is required`
    }
    return null
  },

  email: (value: any, fieldName: string = 'Email'): string | null => {
    if (!value) return null // Let required handle empty values
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(value)) {
      return `${fieldName} must be a valid email address`
    }
    return null
  },

  positiveNumber: (value: any, fieldName: string = 'Amount'): string | null => {
    if (!value) return null // Let required handle empty values
    const numValue = Number(value)
    if (isNaN(numValue) || numValue <= 0) {
      return `${fieldName} must be greater than 0`
    }
    return null
  },

  custom: (
    value: any,
    fieldName: string,
    customValidator?: (value: any, fieldName: string, record: any) => string | null,
    record?: any
  ): string | null => {
    if (typeof customValidator !== 'function') {
      return null
    }
    return customValidator(value, fieldName, record)
  }
}

// Create validation rule
export const createValidationRule = (
  type: ValidationType,
  fieldName: string,
  options: ValidationRuleOptions = {}
): ValidationRule => {
  return {
    type,
    fieldName,
    options,
    validate: (record: any): string | null => {
      const value = record[options.fieldKey || fieldName.toLowerCase()]

      switch (type) {
        case VALIDATION_TYPES.REQUIRED:
          return VALIDATORS.required(value, fieldName)
        case VALIDATION_TYPES.EMAIL:
          return VALIDATORS.email(value, fieldName)
        case VALIDATION_TYPES.POSITIVE_NUMBER:
          return VALIDATORS.positiveNumber(value, fieldName)
        case VALIDATION_TYPES.CUSTOM:
          return VALIDATORS.custom(value, fieldName, options.validator, record)
        case VALIDATION_TYPES.ASYNC:
          // Async validation is handled separately; a sync rule of this type reports nothing here.
          return null
        default:
          return null
      }
    }
  }
}

// Validate record against multiple rules
export const validateRecord = (record: any, validationRules: ValidationRule[] = []): ValidationResult => {
  const errors: Record<string, string> = {}
  let isValid = true

  for (const rule of validationRules) {
    const error = rule.validate(record)
    if (error) {
      const fieldKey = rule.options.fieldKey || rule.fieldName.toLowerCase()
      errors[fieldKey] = error
      isValid = false
    }
  }

  return { isValid, errors }
}

// Validate and show first error
export const validateAndShowErrors = (
  record: any,
  validationRules: ValidationRule[] = [],
  onFieldFocus: ((fieldKey: string) => void) | null = null
): ValidationResult => {
  const { isValid, errors } = validateRecord(record, validationRules)

  if (!isValid) {
    const firstErrorField = Object.keys(errors)[0]
    const firstErrorMessage = errors[firstErrorField]

    // Show toast for the first error
    toast.warning(firstErrorMessage, TOAST_CONFIG)

    // Focus the field if callback provided
    if (onFieldFocus && firstErrorField) {
      onFieldFocus(firstErrorField)
    }

    return { isValid: false, errors, firstErrorField }
  }

  return { isValid: true, errors: {}, firstErrorField: null }
}

// Common validation sets for reuse
export const COMMON_VALIDATION_SETS = {
  // CompanyTeamTable validation - using actual column names
  TEAM_MEMBER: [
    createValidationRule(VALIDATION_TYPES.REQUIRED, 'Name', { fieldKey: 'contact' }),
    createValidationRule(VALIDATION_TYPES.REQUIRED, 'Job Category', { fieldKey: 'category' }),
    createValidationRule(VALIDATION_TYPES.REQUIRED, 'Email Address', { fieldKey: 'email' }),
    createValidationRule(VALIDATION_TYPES.EMAIL, 'Email Address', { fieldKey: 'email' })
  ],

  // DealCommitments validation - using actual column names
  COMMITMENT: [
    createValidationRule(VALIDATION_TYPES.REQUIRED, 'Fund', { fieldKey: 'fund' }),
    createValidationRule(VALIDATION_TYPES.REQUIRED, 'Commitment', { fieldKey: 'amount' }),
    createValidationRule(VALIDATION_TYPES.POSITIVE_NUMBER, 'Commitment', { fieldKey: 'amount' })
  ]
}

// Helper to create custom validation rules for specific needs
// validator signature: (value, fieldName, record) => errorMessage | null
export const createCustomValidationRule = (
  fieldName: string,
  validator: (value: any, fieldName: string, record: any) => string | null,
  fieldKey: string | null = null
): ValidationRule => {
  return createValidationRule(VALIDATION_TYPES.CUSTOM, fieldName, {
    fieldKey: fieldKey || fieldName.toLowerCase(),
    validator
  })
}

// Merge validation rule sets
export const mergeValidationSets = (...sets: ValidationRule[][]): ValidationRule[] => {
  return sets.flat()
}
